import { Globe, type LucideIcon } from 'lucide-react'

import {
  Facebook,
  Instagram,
  LinkedIn,
  Snapchat,
  TikTok,
  Youtube,
  Reddit,
  X
} from '@/assets'
import type { SocialChannelPlatform } from '@/api/types/Common'
import type { IndividualSocialPlatformGroupedResponse } from '@/api/types/Organization'

interface SocialChannelType {
  key: string
  name: string
  icon?: LucideIcon | React.ComponentType
  baseUrl?: string
}

const SOCIAL_PLATFORM_CONFIGS: SocialChannelType[] = [
  { key: 'instagram', name: 'Instagram', icon: Instagram, baseUrl: 'https://www.instagram.com/' },
  { key: 'tiktok', name: 'TikTok', icon: TikTok, baseUrl: 'https://www.tiktok.com/@' },
  { key: 'X', name: 'X', icon: X, baseUrl: 'https://www.x.com/' },
  { key: 'facebook', name: 'Facebook', icon: Facebook, baseUrl: 'https://www.facebook.com/' },
  { key: 'linkedIn', name: 'LinkedIn', icon: LinkedIn, baseUrl: 'https://www.linkedin.com/in/' },
  { key: 'snapchat', name: 'Snapchat', icon: Snapchat, baseUrl: 'https://www.snapchat.com/add/' },
  { key: 'youtube', name: 'YouTube', icon: Youtube, baseUrl: 'https://www.youtube.com/' },
  { key: 'reddit', name: 'Reddit', icon: Reddit, baseUrl: 'https://www.reddit.com/user/' },
  { key: 'website', name: 'Website', icon: Globe },
  { key: 'other', name: 'Other', icon: Globe },
]

export const availableSocialChannels: {
  value: SocialChannelPlatform
  label: string
  icon?: LucideIcon | React.ComponentType
  baseUrl?: string
}[] = Object.entries(SOCIAL_PLATFORM_CONFIGS).map(([_, config]) => ({
  value: config.key as SocialChannelPlatform,
  label: config.name,
  icon: config.icon,
  baseUrl: config.baseUrl,
}))

export const getFormattedSocialChannels = (socialChannels: IndividualSocialPlatformGroupedResponse) => {
  return SOCIAL_PLATFORM_CONFIGS
    .map(config => ({
      ...config,
      channels: socialChannels[config.key as SocialChannelPlatform] || [],
    }))
    .filter(platform => platform.channels.length > 0)
}

export const flattenSocialChannels = (
  socialChannels: IndividualSocialPlatformGroupedResponse
) => {
  const flattened: (
    IndividualSocialPlatformGroupedResponse[keyof IndividualSocialPlatformGroupedResponse][number]
    & SocialChannelType)[] = []

  SOCIAL_PLATFORM_CONFIGS.forEach(config => {
    const channels = socialChannels[config.key as SocialChannelPlatform] || []
    channels.forEach(channel => {
      flattened.push({
        ...config,
        ...channel,
      })
    })
  })

  return flattened
}

export const addSocialChannelEntry = (
  platform: SocialChannelPlatform,
  newUrl: string
): Record<SocialChannelPlatform, string[]> => {
  return {
    [platform]: newUrl ? [newUrl] : [],
  } as Record<SocialChannelPlatform, string[]>
}
